// =============================================================================
// VINUM - Shared Hooks
// Platform-agnostic hooks
// =============================================================================

import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { debounce, throttle } from '../utils';
import { getWsUrl } from '../constants';
import { getAccessToken } from '../api/client';
import { useNotificationStore } from '../stores/notification.store';
import type { Notification } from '../api/notifications.api';

// -----------------------------------------------------------------------------
// useDebounce
// Debounce a value change
// -----------------------------------------------------------------------------

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

// -----------------------------------------------------------------------------
// useDebouncedCallback
// Debounce a callback function
// -----------------------------------------------------------------------------

export function useDebouncedCallback<T extends (...args: unknown[]) => unknown>(
  callback: T,
  delay: number
): T {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  return useMemo(
    () => debounce((...args: unknown[]) => callbackRef.current(...args), delay) as T,
    [delay]
  );
}

// -----------------------------------------------------------------------------
// useThrottledCallback
// Throttle a callback function
// -----------------------------------------------------------------------------

export function useThrottledCallback<T extends (...args: unknown[]) => unknown>(
  callback: T,
  limit: number
): T {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  return useMemo(
    () => throttle((...args: unknown[]) => callbackRef.current(...args), limit) as T,
    [limit]
  );
}

// -----------------------------------------------------------------------------
// usePrevious
// Get the previous value of a variable
// -----------------------------------------------------------------------------

export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

// -----------------------------------------------------------------------------
// useToggle
// Boolean toggle hook
// -----------------------------------------------------------------------------

export function useToggle(initialValue = false): [boolean, () => void, (value: boolean) => void] {
  const [value, setValue] = useState(initialValue);
  const toggle = useCallback(() => setValue((v) => !v), []);
  return [value, toggle, setValue];
}

// -----------------------------------------------------------------------------
// useDisclosure
// For modals, drawers, etc.
// -----------------------------------------------------------------------------

export function useDisclosure(initialOpen = false) {
  const [isOpen, setIsOpen] = useState(initialOpen);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  return { isOpen, open, close, toggle, setIsOpen };
}

// -----------------------------------------------------------------------------
// useLocalStorage (Web only - will be overridden in RN)
// -----------------------------------------------------------------------------

export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }
    },
    [key, storedValue]
  );

  return [storedValue, setValue];
}

// -----------------------------------------------------------------------------
// useAsync
// Handle async operations with loading/error states
// -----------------------------------------------------------------------------

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

interface AsyncActions<T> {
  execute: () => Promise<T | null>;
  reset: () => void;
  setData: (data: T) => void;
}

export function useAsync<T>(
  asyncFunction: () => Promise<T>,
  immediate = false
): AsyncState<T> & AsyncActions<T> {
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    loading: immediate,
    error: null,
  });

  const execute = useCallback(async () => {
    setState({ data: null, loading: true, error: null });
    try {
      const data = await asyncFunction();
      setState({ data, loading: false, error: null });
      return data;
    } catch (error) {
      setState({ data: null, loading: false, error: error as Error });
      return null;
    }
  }, [asyncFunction]);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  const setData = useCallback((data: T) => {
    setState((prev) => ({ ...prev, data }));
  }, []);

  useEffect(() => {
    if (immediate) {
      execute();
    }
  }, [immediate, execute]);

  return { ...state, execute, reset, setData };
}

// -----------------------------------------------------------------------------
// useInterval
// setInterval hook
// -----------------------------------------------------------------------------

export function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay === null) return;

    const id = setInterval(() => savedCallback.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
}

// -----------------------------------------------------------------------------
// useTimeout
// setTimeout hook
// -----------------------------------------------------------------------------

export function useTimeout(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay === null) return;

    const id = setTimeout(() => savedCallback.current(), delay);
    return () => clearTimeout(id);
  }, [delay]);
}

// -----------------------------------------------------------------------------
// useOnClickOutside
// Detect clicks outside an element
// -----------------------------------------------------------------------------

export function useOnClickOutside<T extends HTMLElement>(
  ref: React.RefObject<T>,
  handler: (event: MouseEvent | TouchEvent) => void
) {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }
      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

// -----------------------------------------------------------------------------
// useKeyPress
// Detect key press
// -----------------------------------------------------------------------------

export function useKeyPress(targetKey: string, callback: () => void) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === targetKey) {
        callback();
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [targetKey, callback]);
}

// -----------------------------------------------------------------------------
// useMounted
// Check if component is mounted
// -----------------------------------------------------------------------------

export function useMounted(): boolean {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted;
}

// -----------------------------------------------------------------------------
// useIsMounted
// Get ref to check if mounted (for async operations)
// -----------------------------------------------------------------------------

export function useIsMounted(): () => boolean {
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return useCallback(() => isMountedRef.current, []);
}

// -----------------------------------------------------------------------------
// useUpdateEffect
// useEffect that skips the first render
// -----------------------------------------------------------------------------

export function useUpdateEffect(effect: () => void | (() => void), deps: unknown[]) {
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    return effect();
  }, deps);
}

// -----------------------------------------------------------------------------
// useForceUpdate
// Force a re-render
// -----------------------------------------------------------------------------

export function useForceUpdate(): () => void {
  const [, setTick] = useState(0);
  return useCallback(() => setTick((tick) => tick + 1), []);
}

// =============================================================================
// WebSocket Hooks — Real-time notifications
// =============================================================================

// -----------------------------------------------------------------------------
// useWebSocket
// Generic WebSocket hook with auto-reconnect and heartbeat
// -----------------------------------------------------------------------------

interface WsMessage {
  event: string;
  data: any;
}

interface UseWebSocketOptions {
  /** Whether to attempt connection */
  enabled?: boolean;
  /** Callback for incoming messages */
  onMessage?: (message: WsMessage) => void;
  /** Callback when connection opens */
  onOpen?: () => void;
  /** Callback when connection closes */
  onClose?: () => void;
}

interface UseWebSocketReturn {
  isConnected: boolean;
}

export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const { enabled = true, onMessage, onOpen, onClose } = options;
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const isMountedRef = useRef(true);

  // Keep callbacks in refs to avoid reconnection on callback changes
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const connect = useCallback(() => {
    if (!isMountedRef.current || !enabled) return;

    const token = getAccessToken();
    if (!token) return;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    try {
      const wsUrl = getWsUrl();
      const ws = new WebSocket(wsUrl, [token]);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!isMountedRef.current) return;
        setIsConnected(true);
        reconnectAttemptRef.current = 0;
        onOpenRef.current?.();
      };

      ws.onmessage = (event) => {
        if (!isMountedRef.current) return;
        try {
          const message = JSON.parse(event.data) as WsMessage;
          onMessageRef.current?.(message);
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = (event) => {
        if (!isMountedRef.current) return;
        setIsConnected(false);
        wsRef.current = null;
        onCloseRef.current?.();

        // Don't reconnect if closed intentionally (code 1000) or auth failure (4001-4003)
        if (event.code === 1000 || (event.code >= 4001 && event.code <= 4003)) {
          return;
        }

        // Auto-reconnect with exponential backoff (max 30s)
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptRef.current), 30000);
        reconnectAttemptRef.current++;
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) connect();
        }, delay);
      };

      ws.onerror = () => {
        // Error handling is done in onclose
      };
    } catch {
      // Connection failed, will retry via onclose handler
    }
  }, [enabled]);

  useEffect(() => {
    isMountedRef.current = true;

    if (enabled) {
      connect();
    }

    return () => {
      isMountedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
        wsRef.current = null;
      }
    };
  }, [enabled, connect]);

  return { isConnected };
}

// -----------------------------------------------------------------------------
// useNotificationWebSocket
// Connects WebSocket to the notification store for real-time updates
// Returns isConnected + the latest notification for toast display
// -----------------------------------------------------------------------------

interface UseNotificationWebSocketOptions {
  /** Whether the user is authenticated */
  enabled?: boolean;
  /** Callback when a new notification arrives (for showing toasts) */
  onNotification?: (notification: Notification) => void;
}

export function useNotificationWebSocket(
  options: UseNotificationWebSocketOptions = {}
): { isConnected: boolean } {
  const { enabled = true, onNotification } = options;
  const addNotification = useNotificationStore((s) => s.addNotification);
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  const handleMessage = useCallback(
    (message: WsMessage) => {
      if (message.event === 'notification' && message.data) {
        const notification: Notification = {
          id: message.data.id || '',
          title: message.data.title || '',
          body: message.data.body || '',
          data: message.data.data,
          sentAt: message.data.sentAt || new Date().toISOString(),
          read: false,
          readAt: null,
        };

        // Update store (badge + recent list)
        addNotification(notification);

        // Notify consumer (for toast)
        onNotificationRef.current?.(notification);
      }
    },
    [addNotification]
  );

  const { isConnected } = useWebSocket({
    enabled,
    onMessage: handleMessage,
  });

  return { isConnected };
}
